import { v4 as uuid } from 'uuid';
import { AccountInterface, RpcProvider } from 'starknet';
import {
  getNostraContract,
  getNostraTokenFromFlags,
  getAccountAddressFromId,
  getTokenAmountTransferredInReceipt,
  waitForTransactionReceipt,
} from '../../utils';
import { Decimal } from '../../datastructures';
import { Asset, InvokeResponse } from '../../interfaces';
import { Nullable } from '../../interfaces';
import { DecimalFormat } from '@tempusfinance/decimal';
import { addToast } from '../toast';
import { createAsyncAtom, emitAtomEvent } from '../../lib/state';
import { pauseBalanceRefresh, resumeBalanceRefresh } from './pauseBalanceRefresh';
import { depositInput } from './lendAndBorrowInputAtom';

interface DepositRequest {
  asset: Asset;
  amount: Decimal;
  onBehalfOf: string;
  accountId: number;
  isInterestBearing: boolean;
  isCollateral: boolean;
  txnId: string;
  referralCode: string;
  provider: RpcProvider;
}

interface DepositStatus {
  pending: boolean;
  request: DepositRequest;
  success?: boolean;
  error?: Error;
  getTransactionResponse?: InvokeResponse;
  transactionData?: {
    depositedAmount: Decimal;
  };
  txnId: string;
}

/* interface DepositResponse {
  request: DepositRequest;
  getTransactionResponse?: InvokeTransactionResponse | void;
  getTransactionReceipt?: InvokeTransactionReceiptResponse;
  transactionData?: {
    depositedAmount: Decimal;
    gasFee: Decimal;
  };
  error?: Error;
  txnId: string;
} */

const lendAndBorrowDepositStatusAtom = createAsyncAtom<Nullable<DepositStatus>>(async () => null);

const lendAndBorrowDeposit = async (walletAccount: AccountInterface, request: DepositRequest) => {
  const { asset, amount, accountId, isInterestBearing, isCollateral, txnId, referralCode, provider } = request;

  const nostraToken = getNostraTokenFromFlags(asset, {
    interestBearing: isInterestBearing,
    collateral: isCollateral,
  });

  const nostraTokenContract = getNostraContract(asset, nostraToken.address, walletAccount);

  if (!nostraTokenContract) {
    return null;
  }

  const accountAddress = getAccountAddressFromId(walletAccount.address, accountId);

  const amountFormatted = DecimalFormat.format(amount, {
    style: 'currency',
    currency: asset.token,
    fractionDigits: asset.uiTokenPrecision,
    pad: true,
  });

  // Freeze balance atom refreshes for the duration of the transaction so polling
  // cannot pick up on-chain balance changes before the transaction status is resolved.
  pauseBalanceRefresh();
  try {
    lendAndBorrowDepositStatusAtom.setValue({
      pending: true,
      request,
      txnId,
    });

    let transactionResponse: Nullable<InvokeResponse> = null;
    try {
      transactionResponse = await nostraTokenContract.mint(
        asset.address,
        amount,
        accountAddress,
        referralCode,
        walletAccount,
      );
    } catch (error) {
      lendAndBorrowDepositStatusAtom.setValue({
        pending: false,
        success: false,
        error: new Error('Failed to deposit!'),
        request,
        txnId,
      });

      addToast({
        id: uuid(),
        type: 'error',
        message: `Deposit ${amountFormatted}`,
      });

      return;
    }

    addToast({
      id: uuid(),
      type: 'info',
      message: `Deposit ${amountFormatted}`,
      txId: transactionResponse?.transactionHash,
    });

    if (!transactionResponse) {
      const userRejectError = new Error('Rejected by user.');
      lendAndBorrowDepositStatusAtom.setValue({
        pending: false,
        success: false,
        error: userRejectError,
        request,
        txnId,
      });

      addToast({
        id: uuid(),
        type: 'error',
        message: `Deposit ${amountFormatted}`,
      });

      return;
    }

    const transactionReceipt = await waitForTransactionReceipt(
      transactionResponse.transactionHash,
      walletAccount,
      provider,
    );

    if (!transactionReceipt) {
      const receiptFetchFailed = new Error('Failed to fetch deposit transaction receipt!');
      lendAndBorrowDepositStatusAtom.setValue({
        pending: false,
        success: false,
        error: receiptFetchFailed,
        request,
        txnId,
      });

      addToast({
        id: uuid(),
        type: 'error',
        message: `Deposit ${amountFormatted}`,
        txId: transactionResponse.transactionHash,
      });

      return;
    }

    if (transactionReceipt.isReverted()) {
      const txnReverted = new Error('Transaction reverted!');
      lendAndBorrowDepositStatusAtom.setValue({
        pending: false,
        success: false,
        error: txnReverted,
        request,
        txnId,
      });

      addToast({
        id: uuid(),
        type: 'error',
        message: `Deposit ${amountFormatted}`,
        txId: transactionResponse.transactionHash,
      });
    }

    if (transactionReceipt.isSuccess()) {
      const depositedAmount = getTokenAmountTransferredInReceipt(transactionReceipt, request.asset);

      lendAndBorrowDepositStatusAtom.setValue({
        pending: false,
        success: true,
        request,
        getTransactionResponse: transactionResponse,
        transactionData: {
          depositedAmount,
        },
        txnId,
      });
    }

    emitAtomEvent('lend-and-borrow-deposit');

    depositInput.reset();

    addToast({
      id: uuid(),
      type: 'success',
      message: `Deposited **${amountFormatted}**`,
      txId: transactionResponse.transactionHash,
    });
  } finally {
    resumeBalanceRefresh();
  }
};

export { lendAndBorrowDepositStatusAtom, lendAndBorrowDeposit };
